import React, { Component } from 'react';
import '../../../assets/javascript/canvas-to-blob';
import debounce from 'lodash/debounce';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import { Capability, PlanIds } from '../../data/account';
import {
	CustomLabelMessageStamps,
	PreviewOptions,
	PreviewTypes,
} from '../../data/settings';
import ModalModule from '../../modal';
import { UpgradeMessage } from '../../subscriptionPlan/UpgradeMessage.component';
import UpgradePlanModal from '../../subscriptionPlan/UpgradePlanModal.component';
import UI from '../../ui';
import Utils from '../../utils';
import Util from '../Util';
import PreviewLabelImageModal from './PreviewLabelImageModal.component';
import UploadImageModal from './UploadImageModal.component';

const { Communication, Logger, Session, BillingPlans } = Utils;
const { ModalContext } = ModalModule;
const { prettyPrintPlan } = BillingPlans;
const {
	WrappedButton,
	WrappedButtonType,
	WrappedCheckbox,
	WrappedDropdown,
	WrappedTooltip,
	WrappedInputTextWithLabel,
	WrappedInputTextareaWithLabel,
} = UI;

export default class LabelCustomizationView extends Component {
	constructor(props) {
		super(props);

		const date = new Date();
		const {
			LabelCustomizationImageId = '0',
			PrintImageOnLabelsAndEnvelopes = '0',
			SettingsReference = '',
			SettingsInstructions = '',
		} = Session.getPreferences();

		const hideUpgrade = Session.hasCapability(
			Capability.CanUseBasicLabelDesign
		);
		const upgradePlan = BillingPlans.getPlanByPlanId(PlanIds.OfficeV381);

		this.state = {
			imagesLoaded: false,
			labelCustomizationImageId: LabelCustomizationImageId,
			printImageOnLabelsAndEnvelopes: PrintImageOnLabelsAndEnvelopes,
			settingsReference: decodeURIComponent(SettingsReference),
			settingsInstructions: decodeURIComponent(SettingsInstructions),
			shipDate: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
			upgradePlan,
			hideUpgrade,
		};

		this.debouncedLogPourSettings = debounce(this.logPourSettings, 1000);
	}

	componentDidMount() {
		this.getImageList();
	}

	getImageList = async () => {
		const { showError } = this.context;
		const response = await Communication.getImageList();

		if (Communication.isSuccessful(response)) {
			const { data } = response;

			if (isArray(data) && data.length > 0) {
				const { ImageId, ImageUrl } = data[data.length - 1];
				const labelImage = {
					id: ImageId.toString(),
					url: ImageUrl,
				};

				this.setImageState(labelImage);
			}
		} else if (response.ErrorCode === 4524803) {
			// no images associated with account
			this.setState({ imagesLoaded: false });
		} else {
			showError({ response });
		}
	};

	setImageState = (labelImage, callback) => {
		const { labelCustomizationImageId } = this.state;
		const { showError } = this.context;

		/**
		 * check if the image id is the same as in the preference setting
		 * if user uploads a new image, the id has to be the new labelCustomizationImageId
		 */
		const newLabelId = {
			labelCustomizationImageId:
				labelImage.id !== labelCustomizationImageId
					? labelImage.id
					: labelCustomizationImageId,
		};

		this.setState({
			labelImage,
			imagesLoaded: true,
			...newLabelId,
		});

		if (callback) {
			callback(newLabelId, showError);
		}
	};

	onClickUploadImage = e => {
		const { showModal } = this.context;
		const { labelCustomizationImageId } = this.state;

		showModal({
			component: UploadImageModal,
			data: {
				labelCustomizationImageId,
				completionCallback: this.setImageState,
			},
		});

		e.stopPropagation();
		e.preventDefault();
	};

	onClickPreview = async ({ currentTarget }) => {
		const { showError, showModal } = this.context;
		const { customerId } = Session.getAccountInfo();
		const { EmailTo } = Session.getPreferences();
		const {
			labelCustomizationImageId,
			printImageOnLabelsAndEnvelopes,
			settingsReference,
			settingsInstructions,
			shipDate,
		} = this.state;
		const { type } = currentTarget.dataset;
		const LogPreviewTypes = PreviewOptions.reduce(
			(_logPreviewTypes, option) => {
				_logPreviewTypes[option.type] = option.text;
				return _logPreviewTypes;
			},
			{}
		);

		Logger.logToServer({
			category: 'settings-view-preview',
			preview_type: LogPreviewTypes[type],
		});

		showModal({
			component: PreviewLabelImageModal,
			data: {
				customerID: customerId,
				emailTo: EmailTo,
				labelCustomizationImageId,
				printImageOnLabelsAndEnvelopes,
				settingsReference,
				settingsInstructions,
				shipDate,
				showError,
				type,
			},
		});
	};

	onClickDone = () => {
		const { completionCallback } = this.props;

		if (isFunction(completionCallback)) {
			completionCallback({ close: true });
		}
	};

	onChangeCheckbox = ({ target: { checked } }) => {
		const { showError } = this.context;
		const printImageOnLabelsAndEnvelopes = {
			printImageOnLabelsAndEnvelopes: checked ? '1' : '0',
		};

		this.setState(printImageOnLabelsAndEnvelopes);

		Util.saveNewPreferences(printImageOnLabelsAndEnvelopes, showError);
	};

	onChangeInputText = ({ target: { value, name } }) => {
		const { showError } = this.context;

		this.setState({ [name]: value });

		Util.saveNewPreferences(
			{
				[name]: encodeURIComponent(value),
			},
			showError
		);

		this.debouncedLogPourSettings(name, value);
	};

	onInsertVariable = ({ target: { name }, value }) => {
		const { [name]: inputFieldValue } = this.state;
		const { showError } = this.context;

		this.setState({
			[name]: inputFieldValue === '' ? value : `${inputFieldValue} ${value}`,
		});

		Util.saveNewPreferences(
			{
				[name]:
					inputFieldValue === ''
						? encodeURIComponent(value)
						: encodeURIComponent(`${inputFieldValue} ${value}`),
			},
			showError
		);

		Logger.logToServer({
			category: 'settings-pour-add-variable',
			target_field: name === 'settingsReference' ? 'reference' : 'instructions',
			variable: value.replace(/\[/g, '').replace(/\]/g, ''),
		});

		this.debouncedLogPourSettings(name, value);
	};

	logPourSettings = (name, value) => {
		const { SettingsReference, SettingsInstructions } =
			Session.getPreferences();
		const userPrefValue =
			name === 'settingsReference' ? SettingsReference : SettingsInstructions;
		const encodedValue = encodeURIComponent(value);

		if (userPrefValue !== encodedValue) {
			Logger.logToServer({
				category: 'settings-pour-edit-ref',
				new_value: encodedValue,
			});
		}
	};

	renderExistingImagesContent = () => {
		const { printImageOnLabelsAndEnvelopes, labelImage, hideUpgrade } =
			this.state;
		const contentClassName = hideUpgrade ? 'p-grid' : 'p-grid mask-content';

		return (
			<div className={contentClassName}>
				<div
					className="p-col-12"
					data-qa="settings-commonLabelCustom-chooseAnImage-text"
					{...{ dataqa: 'settings-commonLabelCustom-chooseAnImage-text' }}
				>
					<p>
						Choose an image that will be printed next to the return address on
						your envelopes and shipping labels.
					</p>
				</div>
				<div className="p-col-12">
					<div className="saved-images">
						<img key={labelImage.id} src={labelImage.url} alt="" />
					</div>
				</div>
				<div className="p-col-12">
					<WrappedButton
						className="change-image-button"
						label="Change Image"
						type="button"
						buttonType={WrappedButtonType.Secondary}
						onClick={this.onClickUploadImage}
						dataqa="settings-commonLabelCustom-changeImage"
					/>
					<WrappedButton
						label="Preview Label"
						type="button"
						buttonType={WrappedButtonType.TertiaryGrey}
						onClick={this.onClickPreview}
						data-type={PreviewTypes.Label}
						dataqa="settings-commonLabelCustom-previewLabel"
					/>
					<WrappedButton
						label="Preview Envelope"
						type="button"
						buttonType={WrappedButtonType.TertiaryGrey}
						onClick={this.onClickPreview}
						data-type={PreviewTypes.Envelope}
						dataqa="settings-commonLabelCustom-previewEnvelope"
					/>
				</div>
				<div
					className="p-col-12"
					data-qa="settings-commonLabelCustom-printThisImage-text"
					{...{ dataqa: 'settings-commonLabelCustom-printThisImage-text' }}
				>
					<WrappedCheckbox
						inputId="cb1"
						name="printImageCheckbox"
						value={printImageOnLabelsAndEnvelopes}
						onChange={this.onChangeCheckbox}
						checked={printImageOnLabelsAndEnvelopes === '1'}
						dataqa="settings-printimagecheckbox"
					>
						Print this image on my envelopes and shipping labels.
					</WrappedCheckbox>
				</div>
			</div>
		);
	};

	renderNoImagesContent = () => {
		const { hideUpgrade } = this.state;
		const contentClassName = hideUpgrade ? 'p-grid' : 'p-grid mask-content';

		return (
			<div className={contentClassName}>
				<div
					className="p-col-12 title"
					data-qa="settings-commonLabelCustom-customizeYourLabels-title"
					{...{
						dataqa: 'settings-commonLabelCustom-customizeYourLabels-title',
					}}
				>
					<h1>Customize your labels and envelopes</h1>
				</div>
				<div className="p-col-6 content-left">
					<p>Add a graphic, image, or logo to envelopes and shipping labels.</p>
					<p>Upload an image to get started.</p>
					<WrappedButton
						className="upload-image-button"
						label="Upload Image"
						type="button"
						onClick={this.onClickUploadImage}
						dataqa="settings-commonLabelCustom-uploadImage"
					/>
				</div>
				<div className="p-col-6 content-right">
					<div className="label-customization-sample" />
				</div>
			</div>
		);
	};

	renderOption = option => {
		// Replace all characters that are not alphanumeric, underscore, or hyphen with '_'
		const optionLabelTarget = option.label.replace(/[^a-zA-Z0-9_-]/g, '_');

		return (
			<>
				<div className={`option-label${optionLabelTarget}`}>{option.label}</div>
				<WrappedTooltip
					key={option.label}
					target={`option-label${optionLabelTarget}`}
					tooltip={option.tooltip}
					position="bottom"
				/>
			</>
		);
	};

	renderReturnLabelInstructions = () => {
		const { settingsReference, settingsInstructions } = this.state;

		return (
			<div className="return-label-instructions">
				<div
					className="settings-content-header"
					data-qa="settings-commonLabelCustom-returnLabel-header"
					{...{ dataqa: 'settings-commonLabelCustom-returnLabel-header' }}
				>
					<p>Return Label Instructions</p>
					<hr />
				</div>
				<div
					className="p-grid"
					data-qa="settings-commonLabelCustom-returnLabelBody"
					{...{ dataqa: 'settings-commonLabelCustom-returnLabelBody' }}
				>
					<p
						className="p-col-12"
						data-qa="settings-commonLabelCustom-returnLabelInstructions"
						{...{
							dataqa: 'settings-commonLabelCustom-returnLabelInstructions',
						}}
					>
						Customize the default Reference # and instructions printed at the
						bottom of all Pay-on-Use Return Labels. You can enter static
						content, use the menus to insert dynamic order-specific variables,
						or both.
					</p>
					<div className="settings-row p-col-12">
						<WrappedInputTextWithLabel
							label="Reference #:"
							uiElementConfig={{
								type: 'text',
								name: 'settingsReference',
								value: settingsReference,
								onChange: this.onChangeInputText,
								dataqa: 'settings-commonLabelCustom-referenceInsertVariable',
							}}
						/>
						<WrappedDropdown
							name="settingsReference"
							options={CustomLabelMessageStamps}
							itemTemplate={this.renderOption}
							onChange={this.onInsertVariable}
							placeholder="Insert Variable"
							dataqa="settings-commonLabelCustom-referenceInsertVariable"
						/>
					</div>
					<div className="settings-row p-col-12">
						<WrappedInputTextareaWithLabel
							label="Instructions:"
							uiElementConfig={{
								type: 'text',
								name: 'settingsInstructions',
								value: settingsInstructions,
								onChange: this.onChangeInputText,
								autoResize: true,
								dataqa: 'settings-commonLabelCustom-instructionsInsertVariable',
							}}
						/>
						<WrappedDropdown
							name="settingsInstructions"
							options={CustomLabelMessageStamps}
							itemTemplate={this.renderOption}
							onChange={this.onInsertVariable}
							placeholder="Insert Variable"
							dataqa="settings-commonLabelCustom-instructionsInsertVariable"
						/>
					</div>
					<div className="settings-hint p-col-12">
						Order variables will be ignored for labels printed outside of the
						Orders page.
					</div>
				</div>
			</div>
		);
	};

	renderUpgradeMessage = () => {
		const { upgradePlan } = this.state;
		return (
			<p>
				Upgrade to the {prettyPrintPlan(upgradePlan.Name)} plan to use this
				feature.{' '}
				<a role="button" tabIndex={0} onClick={this.showUpgradePlanModal}>
					Upgrade now.
				</a>
			</p>
		);
	};

	showUpgradePlanModal = () => {
		const { showModal } = this.context;
		const { upgradePlan } = this.state;

		showModal({
			component: UpgradePlanModal,
			data: {
				upgradePlan,
				upgradePrompt: 'upgradePromptLabelCustomization',
				onUpgradePlanCallback: this.onUpgradePlanCallback,
			},
		});
	};

	onUpgradePlanCallback = () => {
		this.setState({
			hideUpgrade: Session.hasCapability(Capability.CanUseBasicLabelDesign),
		});
	};

	render() {
		const { imagesLoaded, hideUpgrade } = this.state;
		const canPrintUSPSReturn = Session.hasCapability(Capability.UspsReturn);

		return (
			<div
				className="settings-common-labelcustomization-view"
				data-qa="ui-button-settings-commonLabelCustom-uploadImage"
				{...{ dataqa: 'ui-button-settings-commonLabelCustom-uploadImage' }}
			>
				<div className="settings-body">
					<div
						className="settings-content-header"
						data-qa="settings-commonLabelCustom-header"
						{...{ dataqa: 'settings-commonLabelCustom-header' }}
					>
						<p>{imagesLoaded ? 'Label Customization' : 'Printed Image'}</p>
						<hr />
					</div>
					{!hideUpgrade ? (
						<UpgradeMessage>{this.renderUpgradeMessage()}</UpgradeMessage>
					) : null}
					{imagesLoaded
						? this.renderExistingImagesContent()
						: this.renderNoImagesContent()}
					{canPrintUSPSReturn ? this.renderReturnLabelInstructions() : null}
				</div>
				{canPrintUSPSReturn ? (
					<div className="settings-content-footer">
						<WrappedButton
							className="settings-button"
							buttonType={WrappedButtonType.Primary}
							label="Done"
							type="button"
							onClick={this.onClickDone}
							dataqa="settings-commonLabelCustom-done"
						/>
						<WrappedButton
							className="settings-button"
							buttonType={WrappedButtonType.Secondary}
							label="Preview Return Label"
							type="button"
							onClick={this.onClickPreview}
							data-type={PreviewTypes.ReturnLabel}
							dataqa="settings-commonLabelCustom-previewReturnLabel"
						/>
					</div>
				) : null}
			</div>
		);
	}
}

LabelCustomizationView.contextType = ModalContext;
